import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { C, ink, surface } from '../lib/theme';
import type { Reason, ReasonCategory } from '../lib/types';
import { ErrorBanner, PrimaryButton, SectionHeader, Skeleton } from '../components/primitives';
import { useToast } from '../components/Toast';
import { IconAlert } from '../components/icons';

const CATEGORY_LABEL: Record<ReasonCategory, string> = {
  churn: 'Churn', contraction: 'Contraction', geral: 'Geral (os dois)', regra: 'Regra de anulação',
};

/**
 * Reasons Manager (PRD §7.6) — ROTA PRÓPRIA de admin (`/admin/motivos`), não uma
 * seção da tela de auditoria: é cadastro de baixíssima frequência e não pertence
 * ao fluxo diário. Form inline, não modal — prioriza simplicidade sobre polish.
 *
 * Organizado em 3 listas (pedido explícito: "acessar as listas de motivos de
 * churn, contract e exceção") — Churn e Contraction mostram também os motivos
 * `geral` (servem pros dois); Regras de Anulação é a categoria que zera o
 * valor, sempre com `is_exception = true` (garantido pelo banco, não só aqui).
 */
export function ReasonsAdmin() {
  const toast = useToast();
  const { data, loading, error, reload } = useAsync(() => api.listReasons(true), []);
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ReasonCategory>('geral');
  const [saving, setSaving] = useState(false);

  async function create() {
    if (!label.trim()) { toast('O motivo precisa de um nome.', 'error'); return; }
    setSaving(true);
    try {
      await api.createReason({
        label: label.trim(), description: description.trim() || undefined,
        category, is_exception: category === 'regra',
      });
      toast(`Motivo "${label.trim()}" criado.`, 'ok');
      setLabel(''); setDescription(''); setCategory('geral'); setAdding(false);
      reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao criar o motivo.', 'error');
    } finally { setSaving(false); }
  }

  async function patch(r: Reason, body: Partial<Reason>) {
    try {
      await api.updateReason(r.id, body as never);
      reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao atualizar.', 'error');
    }
  }

  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', font: 'inherit', fontSize: 13, padding: '9px 12px',
    borderRadius: 10, border: `1px solid ${ink(0.14)}`, background: 'var(--input-bg)',
    color: C.fg, outline: 'none',
  };

  const churnList = (data ?? []).filter((r) => r.category === 'churn' || r.category === 'geral');
  const contractionList = (data ?? []).filter((r) => r.category === 'contraction' || r.category === 'geral');
  const regraList = (data ?? []).filter((r) => r.category === 'regra');

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '30px 30px 90px' }}>
      <Link to="/fila" style={{ fontSize: 12.5, color: ink(0.45), textDecoration: 'none' }}>← voltar pro Hub</Link>

      <div style={{ marginTop: 18 }}>
        <SectionHeader
          title="Motivos e regras"
          right={!adding ? <PrimaryButton onClick={() => setAdding(true)}>+ Novo motivo</PrimaryButton> : undefined}
        />
      </div>

      {adding && (
        <div style={{ ...surface, padding: 18, marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Nome do motivo (ex: Preço)" style={input} autoFocus />
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Descrição (opcional)" style={input} />
          <div>
            <div style={{ fontSize: 11, color: ink(0.5), marginBottom: 7 }}>Categoria</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(Object.keys(CATEGORY_LABEL) as ReasonCategory[]).map((cat) => (
                <button
                  key={cat} type="button" onClick={() => setCategory(cat)}
                  style={{
                    border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 700,
                    padding: '8px 13px', borderRadius: 99,
                    background: category === cat ? (cat === 'regra' ? 'rgba(242,85,90,0.2)' : 'rgba(255,107,71,0.2)') : ink(0.07),
                    color: category === cat ? (cat === 'regra' ? C.churn : C.accent) : ink(0.65),
                  }}
                >{CATEGORY_LABEL[cat]}</button>
              ))}
            </div>
            {category === 'regra' && (
              <div style={{ fontSize: 11.5, color: ink(0.45), marginTop: 8, lineHeight: 1.5 }}>
                Regra de anulação: eventos classificados com este motivo saem do total de Perda Confirmada, mesmo continuando auditados como churn/contraction real.
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 9, marginTop: 4 }}>
            <PrimaryButton onClick={create} disabled={saving}>{saving ? 'gravando…' : 'Criar motivo'}</PrimaryButton>
            <button type="button" onClick={() => { setAdding(false); setLabel(''); setDescription(''); setCategory('geral'); }} style={{
              border: `1px solid ${ink(0.14)}`, background: 'transparent', color: ink(0.55), cursor: 'pointer',
              font: 'inherit', fontSize: 13, fontWeight: 600, padding: '9px 16px', borderRadius: 12,
            }}>Cancelar</button>
          </div>
        </div>
      )}

      {loading && <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[0, 1, 2].map((i) => <Skeleton key={i} height={54} radius={12} />)}</div>}
      {error && <ErrorBanner message={error} onRetry={reload} />}

      {data && data.length === 0 && !adding && (
        <div style={{ ...surface, padding: '40px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Nenhum motivo cadastrado</div>
          <div style={{ fontSize: 13, color: ink(0.5), maxWidth: 460, margin: '0 auto' }}>
            Sem motivos, o classificador dos eventos aparece vazio e o gráfico de Top Motivos fica sem dado.
            Cadastre os motivos que o time usa pra classificar churn e contraction.
          </div>
        </div>
      )}

      {data && data.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
          <ReasonGroup title="Motivos de Churn" items={churnList} patch={patch} />
          <ReasonGroup title="Motivos de Contraction" items={contractionList} patch={patch} />
          <ReasonGroup title="Regras de Anulação" items={regraList} patch={patch} />
        </div>
      )}
    </div>
  );
}

function ReasonGroup({ title, items, patch }: {
  title: string; items: Reason[]; patch: (r: Reason, body: Partial<Reason>) => void;
}) {
  if (items.length === 0) return null;
  return (
    <section>
      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: ink(0.42), marginBottom: 9 }}>
        {title} · {items.length}
      </div>
      <div style={{ ...surface, overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 100px 90px', gap: 14, padding: '11px 18px',
          borderBottom: `1px solid ${ink(0.08)}`, fontSize: 10, fontWeight: 800,
          letterSpacing: '0.1em', textTransform: 'uppercase', color: ink(0.35),
        }}>
          <span>Motivo</span><span style={{ textAlign: 'center' }}>Categoria</span><span style={{ textAlign: 'center' }}>Ativo</span>
        </div>
        {items.map((r) => (
          <div key={r.id} style={{
            display: 'grid', gridTemplateColumns: '1fr 100px 90px', gap: 14, padding: '13px 18px',
            borderBottom: `1px solid ${ink(0.05)}`, alignItems: 'center',
            opacity: r.active ? 1 : 0.45,
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 650, color: ink(0.9) }}>
                {r.category === 'regra' && <span style={{ marginRight: 6, display: 'inline-flex', verticalAlign: -2, color: C.amber }}><IconAlert size={12} /></span>}{r.label}
              </div>
              {r.description && <div style={{ fontSize: 11.5, color: ink(0.4), marginTop: 2 }}>{r.description}</div>}
            </div>
            <div style={{ textAlign: 'center', fontSize: 11, color: ink(0.5) }}>{CATEGORY_LABEL[r.category]}</div>
            <div style={{ textAlign: 'center' }}>
              <Toggle on={r.active} onChange={(v) => patch(r, { active: v })} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Toggle({ on, onChange, disabled }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)}
      style={{
        width: 38, height: 21, borderRadius: 99, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', padding: 0,
        background: on ? C.accent : ink(0.14), position: 'relative', transition: 'background 0.2s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: on ? 20 : 3, width: 15, height: 15, borderRadius: '50%',
        background: '#fff', transition: 'left 0.2s ease',
      }} />
    </button>
  );
}
